import { Pipe, PipeTransform } from '@angular/core';

/**
 * ServiceStatusPipe
 *
 * Centralizes all status-name normalization and CSS-class generation so that
 * templates (and the parent component) no longer need to scatter the same
 * switch/if chains across multiple files.
 *
 * Usage:
 *   <!-- Display label -->
 *   {{ service.currentStatusName | serviceStatus }}
 *
 *   <!-- CSS class suffix for status badge -->
 *   class="status-badge status-{{ service.currentStatusName | serviceStatus:'class' }}"
 */

/** Maps raw API status strings → canonical display names. */
const STATUS_NAME_MAP: Record<string, string> = {
    // numeric fall-through from old API
    '0': 'Operational',
    '1': 'Operational',
    '2': 'Degraded Performance',
    '3': 'Partial Outage',
    '4': 'Major Outage',
    '5': 'Scheduled Maintenance',
    // verbose API names
    DegradedPerformance: 'Degraded Performance',
    PartialServiceOutage: 'Partial Outage',
    'Partial Outage': 'Partial Outage',
    'Partial outage': 'Partial Outage',
    ServiceUnderMaintenance: 'Scheduled Maintenance',
    'Scheduled Maintenance': 'Scheduled Maintenance',
    // canonical names (pass-through)
    Operational: 'Operational',
    Degraded: 'Degraded Performance',
    Outage: 'Major Outage',
    Maintenance: 'Scheduled Maintenance',
};

export type ServiceStatusFormat = 'name' | 'class';

@Pipe({
    name: 'serviceStatus',
    pure: true,
    standalone: true,
})
export class ServiceStatusPipe implements PipeTransform {
    transform(value: string | number | null | undefined, format: ServiceStatusFormat = 'name'): string {
        const key = value == null ? '' : String(value);
        const canonical = this.resolveCanonicalStatus(key);

        // If requesting class, return canonical status (lower cased) so CSS matches.
        if (format === 'class') {
            return canonical.toLowerCase().replaceAll(/\s+/g, '-');
        }

        // Return original string if it is textual, else return canonical if it was purely numeric
        const numericValue = Number(key);
        return Number.isNaN(numericValue) && key.trim() !== '' ? key : canonical;
    }

    private resolveCanonicalStatus(key: string): string {
        switch (key) {
            case '0':
            case '1':
                return 'Operational';
            case '2':
                return 'Degraded Performance';
            case '3':
                return 'Partial Outage';
            case '4':
                return 'Major Outage';
            case '5':
                return 'Scheduled Maintenance';
            default:
                break;
        }

        const lowerKey = key.toLowerCase();
        if (lowerKey.includes('maintenance')) {
            return 'Scheduled Maintenance';
        }
        if (lowerKey.includes('partial')) {
            // "Partial outage", "PartialServiceOutage"
            return 'Partial Outage';
        }
        if (lowerKey.includes('degraded')) {
            // "DegradedPerformance"
            return 'Degraded Performance';
        }
        if (lowerKey.includes('major') || lowerKey.includes('outage')) {
            // Any other outage
            return 'Major Outage';
        }
        if (lowerKey.includes('operational')) {
            return 'Operational';
        }

        // Fallback to exact map if not caught by keywords
        return STATUS_NAME_MAP[key] ?? 'Operational';
    }
}
